/* eslint-disable max-lines */
import { useCallback, useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import styled from 'styled-components'
import Tooltip from 'components/Tooltip'
import { color } from 'styles/colors'

import { useSessionContext } from 'hooks/SessionContext'
import LockIcon from '@mui/icons-material/LockOutlined'
import UnlockIcon from '@mui/icons-material/LockOpenOutlined'

import ServerlessApi from 'services/ServerlessApi'

import { screenSize } from 'styles/shared'

import {accessibleFeatures} from 'helpers/features'

import { Feature, DealerFeatures } from 'types/Feature'

const FeaturesContext = createContext<any>(null) // eslint-disable-line @typescript-eslint/no-explicit-any

export const FeaturesContextProvider = ({ children }: { children: ReactNode }) => {
  const { session, isSessionLoaded } = useSessionContext()
  const location = useLocation()
  const navigate = useNavigate()

  const [activeFeatures, setActiveFeatures] = useState<string[]>([])
  const [publishedFeatures, setPublishedFeatures] = useState<string[]>([])
  const [premiumFeatures, setPremiumFeatures] = useState<string[]>([])
  const [tier, setTier] = useState<string | null>(null)
  const [FreeTrial, setFreeTrial] = useState(false)
  const [FreeTrialEndDate, setFreeTrialEndDate] = useState(null)
  const [areFeaturesLoaded, setFeaturesLoaded] = useState(false)
  const [featureInfo] = useState<Array<{ featureCode: string, endDate: string | null, upgradeMsg: string }>>([])

  useEffect(() => {
    if (!isSessionLoaded) {
      return
    }
    
    ServerlessApi.Features.getDealerBetaFeature(session.dealerId)
      .then((res: unknown) => {
        const dealerFeatures = res as DealerFeatures
        const allFeatures = dealerFeatures.features
        const features = accessibleFeatures(session, session.dealerPlan.tier, dealerFeatures)

        setActiveFeatures(features.activeFeatureCodes)
        setPublishedFeatures(features.publishedFeatureCodes)
        setPremiumFeatures(features.premiumFeatureCodes)
        setTier(session.dealerPlan.tier)
        setFreeTrial(session.dealerPlan.in_trial)
        setFreeTrialEndDate(session.dealerPlan.trial_expiration)
        setFeaturesLoaded(true)
        
        allFeatures.filter((feature: Feature) => features.publishedFeatureCodes.includes(feature.name)).forEach((feature: Feature) => {
          featureInfo.push({ featureCode: feature.name, endDate: feature.freeTrialEndDate, upgradeMsg: feature.upgradeMessage })
        })
        
      })
      .catch(() => {
        setActiveFeatures([])
        setPublishedFeatures([])
        setPremiumFeatures([])
        setTier(null)
        setFeaturesLoaded(true)
      })

  }, [location.pathname, location.search, session, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasAccess = useCallback((featureCode: string) => {
    if (areFeaturesLoaded === false || activeFeatures.length === 0) {
      return false
    }

    if (activeFeatures.includes(featureCode)) {
      return true
    } else {
      return false
    }
  }, [areFeaturesLoaded, activeFeatures])

  const showUpgrade = useCallback((featureCode: string) => {
    if (session.isFsr || session.isAdmin || !areFeaturesLoaded || tier !== 'standard' || inFreeTrial(featureCode)) {
      return false
    }

    if (tier === 'standard' && premiumFeatures.includes(featureCode)) {
      return true
    } else {
      return false
    }
  }, [areFeaturesLoaded, premiumFeatures, tier]) // eslint-disable-line react-hooks/exhaustive-deps

  const inFreeTrial = useCallback((featureCode: string) => {
    if (session.isFsr || session.isAdmin || !areFeaturesLoaded || tier !== 'standard') {
      return false
    }

    if (premiumFeatures.includes(featureCode) && activeFeatures.includes(featureCode)) {
      return true
    } else {
      return false
    }
  }, [areFeaturesLoaded, premiumFeatures, activeFeatures]) // eslint-disable-line react-hooks/exhaustive-deps

  const showFeature = useCallback((featureCode: string) => {
    if (publishedFeatures.includes(featureCode) && areFeaturesLoaded) {
      if (inFreeTrial(featureCode) || showUpgrade(featureCode) || hasAccess(featureCode)) {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }, [areFeaturesLoaded, publishedFeatures]) // eslint-disable-line react-hooks/exhaustive-deps

  const hoverTextMessage = useCallback((featureCode: string, defaultMessage: string) => {
    const feature = featureInfo.find((feature) => feature.featureCode === featureCode)
    const upgradeMessage = feature?.upgradeMsg || ''

    if (session.isFsr || session.isAdmin || !areFeaturesLoaded) {
      return ''
    } else if (showUpgrade(featureCode)) {
      return upgradeMessage
    } else if (inFreeTrial(featureCode)) {
      if (feature?.endDate) {
        return `Available during Diagnostics Free-Trial until ${new Date(feature.endDate).toDateString()}. ${upgradeMessage}`.trim()
      } else {
        return `Available during Diagnostics Free-Trial. ${upgradeMessage}`.trim()
      }
    } else {
      return defaultMessage
    }
  }, [inFreeTrial, showUpgrade]) // eslint-disable-line react-hooks/exhaustive-deps

  const LockIcon = ({
    featureCode,
    defaultHover = '',
    featureComponentType = 'card',
    canShowTooltip = true
  }: {
    featureCode: string
    defaultHover?: string
    featureComponentType?: string
    canShowTooltip?: boolean
  }) => {
    if (
      session.isFsr ||
      session.isAdmin ||
      tier !== 'standard' ||
      !areFeaturesLoaded
    ) {
      return <></>
    }
    if (showFeature(featureCode) && showUpgrade(featureCode)) {
      return canShowTooltip ? (
        <Tooltip
          title={hoverTextMessage(featureCode, defaultHover)}
        >
          <S.BackgroundCircle feature_component_type={featureComponentType}>
            <S.LockIcon />
          </S.BackgroundCircle>
        </Tooltip>
      ) : (
        <S.BackgroundCircle feature_component_type={featureComponentType}>
          <S.LockIcon />
        </S.BackgroundCircle>
      )
    } else if (showFeature(featureCode)) {
      return canShowTooltip ? (
        <Tooltip
          title={hoverTextMessage(featureCode, defaultHover)}
        >
          <S.BackgroundCircle feature_component_type={featureComponentType}>
            <S.UnlockIcon />
          </S.BackgroundCircle>
        </Tooltip>
      ) : (
        <S.BackgroundCircle feature_component_type={featureComponentType}>
          <S.UnlockIcon />
        </S.BackgroundCircle>
      )
    } else {
      return <></>
    }
  }

  const InfoIcon = ({ featureCode }: { featureCode: string }) => {
    if (
      session.isFsr ||
      session.isAdmin ||
      !areFeaturesLoaded
    ) {
      return <></>
    }
    if (showFeature(featureCode) || showUpgrade(featureCode)) {
      const feature = featureInfo.find((feature) => feature.featureCode === featureCode)

      return (
        <Tooltip
          title={
            <span data-testid="features-info-tooltip">
              {feature?.upgradeMsg}
            </span>
          }
        >
          <S.Icon
            src="/img/alert-rth-spirte-icon/device_status_info.svg"
            alt="device status icon"
            tabIndex={0}
            data-testid="features-info-icon"
          />
        </Tooltip>
      )
    } else {
      return <></>
    }
  }

  return (
    <FeaturesContext.Provider value={{
      activeFeatures,
      publishedFeatures,
      hasAccess,
      showUpgrade,
      inFreeTrial,
      hoverTextMessage,
      LockIcon,
      tier,
      showFeature,
      areFeaturesLoaded,
      InfoIcon,
      FreeTrial,
      FreeTrialEndDate
    }}
    >
      {children}
    </FeaturesContext.Provider>
  )
}

export const useFeaturesContext = () => useContext(FeaturesContext)

const iconStyle = `
  font-size: 18px;
`
const backgroundStyleCorner = `
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: ${color('border')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const backgroundStyleInline = `
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const S = {
  LockIcon: styled(LockIcon)`
    color: ${color('LockedIcon')};
    ${iconStyle}
  `,
  UnlockIcon: styled(UnlockIcon)`
    color: ${color('UnlockedIcon')};
    ${iconStyle}
  `,
  /* eslint-disable camelcase */
  BackgroundCircle: styled.div<{ feature_component_type: string }>`
    position: ${({ feature_component_type }) => feature_component_type !== 'title' ? 'absolute' : 'relative'};
    top: ${({ feature_component_type }) => feature_component_type === 'card' ? '-5px' : feature_component_type === 'title' ? '0px' : '-10px'};
    right: ${({ feature_component_type }) => feature_component_type === 'card' ? '-5px' : '-10px'};
    ${({ feature_component_type }) => feature_component_type !== 'title' ? backgroundStyleCorner : backgroundStyleInline};
  `,
  /* eslint-enable camelcase */
  Icon: styled.img`
    width: 27px;
    height: 27px;
    margin-left: 10px;

    @media ${screenSize.small} {
      width: 19px;
      height: 19px;
      margin-left: 5px;
    }
  `
}
